<template>
  <div class="leaderboard w-[100lvw] h-[100lvh] bg-cw-orange flex flex-col">
    <div class="bg-cw-dark-alt/80 h-screen w-screen absolute z-0 inset-0"></div>
    <div class="flex z-1 p-[30px]">
      <!-- LEFT SIDEBAR -->
      <div class="flex-3">
        <BaseButton background-color="var(--color-cw-olive)" border-color="#323617" @click="closeLeaderboard">
          <ArrowFatLeftIcon />
        </BaseButton>
      </div>

      <!-- MAIN CONTENT -->
      <div class="flex-6 flex pt-[10vh]">
        <!-- GLOBAL LEADERBOARD -->
        <div class="min-w-[400px] w-full bg-cw-dark rounded-3xl px-9 md:px-10 pt-8 md:pt-8 pb-8 md:pb-10 bounce-in mx-auto max-h-[70vh] overflow-auto">
          <div v-if="isLoading" class="flex justify-center items-center h-40">
            <Loader />
          </div>
          <div v-else-if="errorMessage" class="text-center text-cw-red">
            <p>{{ errorMessage }}</p>
          </div>
          <div v-else class="grid grid-cols-1 gap-y-3">
            <div class="mb-6">
              <h2 class="text-white font-bold text-xl md:text-2xl">Leaderboard (Top 10)</h2>
            </div>

            <div v-for="player in globalLeaderboard" :key="player.user.id" class="flex items-center justify-between py-3 px-6 rounded-2xl" :class="player.user.id === user?.id ? 'bg-[#FFBA66]' : 'bg-[#2D2720]'">
              <div class="flex items-center gap-3">
                <BaseAvatar :src="player.user.avatarUrl" :size="40" />
                <div>
                  <p class="font-bold text-sm md:text-base" :class="user?.id === player.user.id ? 'text-[#2D2720]' : 'text-white'">{{ player.user.username }} {{ user?.id === player.user.id ? '(You)' : '' }}</p>
                </div>
              </div>
              <div class="text-right flex justify-end items-center gap-3">
                <p class="font-bold text-sm md:text-base" :class="[user?.id === player.user.id ? 'text-[#2D2720]' : 'text-[#AA9A88]']">{{ formatNumber(player.totalPoints || 0) }} pts</p>
                <PositionOneIcon v-if="player.rank === 1" />
                <PositionTwoIcon v-else-if="player.rank === 2" />
                <PositionThreeIcon v-else-if="player.rank === 3" />
                <div v-else class="flex items-center justify-center w-8 h-8 rounded-full font-bold text-[10px] bg-[#1E1A15] border-2 border-[#3C342A]">
                  {{ formatDigitToPosition(player.rank) }}
                </div>
              </div>
            </div>

            <div v-if="globalLeaderboard.length === 0" class="text-center py-8">
              <p class="text-[#999999]">No players found</p>
            </div>
          </div>
        </div>
      </div>

      <!-- ACTIONS -->
      <div class="flex-3 flex flex justify-end h-full">
        <div class="actions bounce-in">
          <BaseButton class="px-6" background-color="var(--color-cw-indigo)" border-color="#1F2547" @click="refreshData">
            <Loader v-if="isLoading" />
            <p v-else class="!text-sm md:!text-base xl:!text-xl lg:text-base font-bold">Refresh</p>
          </BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useHelpers } from '@/composables/useHelpers'
import ArrowFatLeftIcon from '@/assets/svgs/icons/arrow-fat-left.svg'
import PositionOneIcon from '@/assets/svgs/icons/position-one.svg'
import PositionTwoIcon from '@/assets/svgs/icons/position-two.svg'
import PositionThreeIcon from '@/assets/svgs/icons/position-three.svg'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseAvatar from '@/components/base/BaseAvatar.vue'
import Loader from '@/components/interface/Loader.vue'
import MiscellaneousService from '@/services/api/leaderboard-service'
import type { LeaderboardEntryT } from '@/types/User'
import { useAppStore } from '@/stores/app'
import { useUserStore } from '@/stores/user'

const { showLeaderboard } = storeToRefs(useAppStore())
const { user } = storeToRefs(useUserStore())
const { showToast, formatDigitToPosition, formatNumber } = useHelpers()

// For this component, we'll use a prop or emit to control visibility
defineEmits<{
  close: []
}>()

const closeLeaderboard = () => {
  showLeaderboard.value = false
}

const isLoading = ref(false)
const errorMessage = ref('')
const globalLeaderboard = ref<LeaderboardEntryT[]>([])

const fetchLeaderboard = async () => {
  try {
    isLoading.value = true
    errorMessage.value = ''
    const { data } = await MiscellaneousService.getLeaderboard()
    globalLeaderboard.value = data || []
  } catch (error: any) {
    errorMessage.value = error?.data?.message || 'Failed to load leaderboard'
    showToast('Failed to load global leaderboard', 'error')
  } finally {
    isLoading.value = false
  }
}

const refreshData = () => {
  fetchLeaderboard()
}

onMounted(() => {
  fetchLeaderboard()
})
</script>
